import { z } from "zod";

/**
 * The slice of `AgentToolConfig.config` where the tool's author declares that it
 * only READS — a lookup, a status check — and so cannot change anything a caller
 * could later find changed.
 *
 * It exists because the tool's type cannot say so: a workflow, a plugin action,
 * an MCP tool and an AI task can each write, so the voice relay treats every one
 * of them as writing, and a model that holds the conversation for a write holds
 * it for a lookup too. Only the author knows which one this row is.
 *
 * Absent is OFF: every row written before the key existed runs as it always
 * has, so no backfill is needed. It only ever WIDENS what may run without a
 * hold — it never makes a read-only type writing — and a tool the caller must
 * confirm, or a transfer, ignores it (`resolve-agent-tool-effect.ts` in the
 * voice relay).
 *
 * DEGRADE, NEVER BLANK, as `dispatch.schema.ts` does: a malformed value drops to
 * OFF, and the rest of the blob passes through for whoever reads it next.
 */
export const AgentToolReadOnlySchema = z
  .object({
    /** The author's word that running this tool changes nothing outside the call. */
    readOnly: z.boolean().optional().catch(undefined)
  })
  .passthrough();

export type AgentToolReadOnly = z.infer<typeof AgentToolReadOnlySchema>;

/** Whether a stored `AgentToolConfig.config` blob declares the tool read-only. Never throws. */
export function readToolIsReadOnly(rawConfig: unknown): boolean {
  const parsed = AgentToolReadOnlySchema.safeParse(rawConfig);
  return parsed.success && parsed.data.readOnly === true;
}
